import { Carousel, Lightbox, LightboxItem } from '@design-system';
import classNames from 'classnames';
import { CSSProperties, useMemo } from 'react';
import { render } from 'storyblok-rich-text-react-renderer-ts';

import { MultiImageStoryblok } from '../types/generated-storyblok';
import { TrackEvent } from '../utils/analytics';
import { isStoryblokRichTextPresent } from '../utils/renderStoryblokRichText';
import { Image } from './Image';
import { ImageDetail } from './Image/ImageDetail';
import { ImageWithProducts } from './Image/ImageWithProducts';
import { InlineVideo } from './InlineVideo/InlineVideo';

interface Blok extends MultiImageStoryblok {
  analytics?: Analytics;
}

const GRID_MAX_ITEMS = 3;
const DEFAULT_CAROUSEL_COLUMNS = 1;
const GAP = 24;

const calcWidth = (cols: number) =>
  cols <= 1 ? '100%' : `calc((100% - ${GAP * (cols - 1)}px) / ${cols})`;

const columnItemMaxWidth = (
  containerSize: MultiImageStoryblok['containerSize'],
  itemsCount: number,
): { sm: string; md: string; lg: string } => {
  const lgCols = itemsCount >= 3 ? 3 : itemsCount;

  switch (containerSize) {
    case 'wide':
      return {
        sm: '100%',
        md: calcWidth(itemsCount >= 3 ? 2 : 1),
        lg: calcWidth(lgCols),
      };
    case 'ultrawide':
    case 'max':
      return {
        sm: '100%',
        md: calcWidth(itemsCount >= 2 ? 2 : 1),
        lg: calcWidth(lgCols),
      };
    default:
      return { sm: '100%', md: '100%', lg: '100%' };
  }
};

export const ContentBlockImage = ({ blok }: { blok: Blok }) => {
  const isCarousel = blok.layout === 'carousel';
  const items = useMemo(
    () => (isCarousel ? blok.images : blok.images.slice(0, GRID_MAX_ITEMS)),
    [blok.images, isCarousel],
  );
  const maxWidth = columnItemMaxWidth(blok.containerSize, items.length);
  const carouselColumns =
    Number(blok.carouselColumns) || DEFAULT_CAROUSEL_COLUMNS;

  const lightboxItems: LightboxItem[] = useMemo(
    () =>
      items.map((item, key) => {
        if (item.component === 'inlineVideo') {
          return {
            key,
            media:
              item.platform === 'youtube'
                ? {
                    id: item.videoId,
                    type: 'YOUTUBE',
                    // Lightbox only supports '16/9' | '9/16'; bucket every
                    // non-landscape Storyblok ratio ('9/16', '4/5', '1/1')
                    // into '9/16' rather than forcing them to landscape.
                    ratio: item.ratio === '16/9' ? '16/9' : '9/16',
                  }
                : {
                    id: item.videoId,
                    type: 'VIMEO',
                  },
            copyright: item.cover.copyright,
          };
        }

        return {
          key,
          media: {
            alt: item.image.alt,
            src: item.image.filename,
            type: 'IMAGE',
          },
          copyright: item.image.copyright,
        };
      }),
    [items],
  );

  const renderMedia = (
    media: Blok['images'][number],
    index: number,
    lightbox: { openOnSlide: (index: number) => void },
  ) => (
    <>
      {media.component === 'inlineVideo' ? (
        <InlineVideo
          autoplay={media.autoplay}
          className="flex-1"
          cover={media.cover.filename}
          onPlay={() => {
            if (blok.analytics) {
              TrackEvent(`${blok.analytics.page} video clicked`, {
                ...blok.analytics,
                title: media.alt || media.cover?.alt || '',
              });
            }
          }}
          platform={media.platform}
          playButton={media?.playButton ?? false}
          ratio={media.ratio}
          requiresAuthToPlay={media.requiresAuthToPlay}
          title={media.cover?.alt ?? ''}
          videoId={media.videoId}
        />
      ) : null}

      {media.component === 'imageWithProducts' ? (
        <ImageWithProducts blok={media} />
      ) : null}

      {media.component === 'imageDetail' ? <ImageDetail blok={media} /> : null}

      {media.component === 'image' ? (
        <figure className="group">
          <Image
            alt={media.image?.alt || media.image.filename}
            className="overflow-hidden rounded-sm"
            hoverEffect
            onClick={() => {
              lightbox.openOnSlide(index);
              if (blok.analytics) {
                TrackEvent(`${blok.analytics.page} image clicked`, {
                  ...blok.analytics,
                  title: media.image?.alt || '',
                });
              }
            }}
            size={{
              sm: { span: 12, ratio: 'intrinsic' },
              md: { span: 12, ratio: 'intrinsic' },
              lg: { span: 12, ratio: 'intrinsic' },
            }}
            src={media.image.filename}
          />
          {isStoryblokRichTextPresent(media.caption) && (
            <figcaption className="aa-prose mt-3 text-xs text-neutral-4">
              {render(media.caption)}
            </figcaption>
          )}
        </figure>
      ) : null}
    </>
  );

  return (
    <Lightbox items={lightboxItems}>
      {(lightbox) => (
        <div
          key={blok._uid}
          className={classNames('mx-auto w-full', {
            'max-w-xl': blok.containerSize === 'wide',
            'max-w-5xl': blok.containerSize === 'ultrawide',
          })}
        >
          {isCarousel ? (
            <div
              // The Carousel computes its own side padding assuming it's a
              // direct full-bleed child of the page; neutralize that here
              // since it's already nested inside the page's own padded
              // content column, which would otherwise double the inset.
              style={
                {
                  '--website-max-width': '100%',
                  '--website-paddings': '0px',
                } as CSSProperties
              }
            >
              <Carousel
                arrowPosition="middle"
                items={items.map((media, index) => (
                  <div key={media._uid} className="w-full">
                    {renderMedia(media, index, lightbox)}
                  </div>
                ))}
                perPage={carouselColumns}
                perMove={1}
                gap={6}
                mobileGap={3}
                loop
                pagination
              />
            </div>
          ) : (
            <div className="flex w-full flex-wrap items-start justify-center gap-6">
              {items.map((media, index) => (
                <div
                  key={media._uid}
                  className={classNames(
                    'w-full',
                    'max-w-[var(--sm)] md:max-w-[var(--md)] lg:max-w-[var(--lg)]',
                  )}
                  style={
                    {
                      '--sm': maxWidth.sm,
                      '--md': maxWidth.md,
                      '--lg': maxWidth.lg,
                    } as CSSProperties
                  }
                >
                  {renderMedia(media, index, lightbox)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Lightbox>
  );
};
